import React, { useState, useEffect, useCallback } from 'react';

// Super Admin → Subscriptions
//   Orders       checkouts placed by firms; "Mark paid" applies the order (same code a gateway will call)
//   Promo codes  create / switch off / delete discount codes
//   Price list   price per user per month, GST, offered durations (+ optional discount per duration), user range

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fd = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function SubscriptionAdmin({ T, saFetch, showToast }) {
  const [tab, setTab] = useState('orders');
  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 8 }}>Subscriptions</h1>
      <p style={{ fontSize: 14, color: T.text2, marginBottom: 20 }}>Orders from firms, promo codes and the price list used by the Renew / Upgrade checkout.</p>
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 20 }}>
        {[['orders', 'Orders'], ['promos', 'Promo codes'], ['pricing', 'Price list']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: tab === k ? T.orange : T.text2, borderBottom: `2px solid ${tab === k ? T.orange : 'transparent'}`, marginBottom: -1 }}>{l}</button>
        ))}
      </div>
      {tab === 'orders' && <Orders T={T} saFetch={saFetch} showToast={showToast} />}
      {tab === 'promos' && <Promos T={T} saFetch={saFetch} showToast={showToast} />}
      {tab === 'pricing' && <Pricing T={T} saFetch={saFetch} showToast={showToast} />}
    </div>
  );
}

const card = (T) => ({ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12 });
const th = (T) => ({ textAlign: 'left', padding: '12px 14px', fontSize: 11, fontWeight: 700, color: T.text2, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' });
const td = { padding: '12px 14px', fontSize: 13, verticalAlign: 'top' };
const small = (bg, fg) => ({ padding: '5px 12px', borderRadius: 7, border: 'none', background: bg, color: fg, fontSize: 12, fontWeight: 600, cursor: 'pointer' });
const inputS = (T) => ({ width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, outline: 'none' });
const labelS = (T) => ({ display: 'block', fontSize: 11, fontWeight: 600, color: T.text2, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' });

function Orders({ T, saFetch, showToast }) {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [paying, setPaying] = useState(null);   // order being marked paid
  const [ref, setRef] = useState('');

  const load = useCallback(async () => {
    const res = await saFetch(`/subscription-orders?status=${status}`);
    setRows(res.ok ? await res.json() : []);
  }, [saFetch, status]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    const res = await saFetch(path, { method: 'POST', body: JSON.stringify(body || {}) });
    const b = await res.json().catch(() => ({}));
    showToast(b.message || (res.ok ? 'Done' : 'Failed'), res.ok ? 'success' : 'error');
    setPaying(null); setRef(''); load();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['pending', 'paid', 'cancelled', 'all'].map(s => (
          <button key={s} onClick={() => setStatus(s)} style={{ ...small(status === s ? T.navy : T.white, status === s ? '#fff' : T.text2), border: `1px solid ${status === s ? T.navy : T.border}`, textTransform: 'capitalize' }}>{s}</button>
        ))}
      </div>
      <div style={{ ...card(T), overflowX: 'auto' }}>
        {rows === null ? <p style={{ padding: 24, color: T.text2, fontSize: 13 }}>Loading…</p> : rows.length === 0 ? <p style={{ padding: 24, color: T.text2, fontSize: 13 }}>No {status === 'all' ? '' : status} orders.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead><tr>{['Order', 'Firm', 'Item', 'Amount', 'Billing', 'Status', ''].map(h => <th key={h} style={th(T)}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(o => (
                <tr key={o.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={td}><b>{o.order_no}</b><div style={{ fontSize: 11, color: T.text2 }}>{fd(o.created_at)} · {o.pay_method || '—'}</div></td>
                  <td style={td}>{o.firm_name}<div style={{ fontSize: 11, color: T.text2 }}>{o.firm_email}</div></td>
                  <td style={td}>{o.description}{o.promo_code && <div style={{ fontSize: 11, color: '#15803d' }}>Promo {o.promo_code} (−{inr(o.discount)})</div>}</td>
                  <td style={td}><b>{inr(o.total)}</b><div style={{ fontSize: 11, color: T.text2 }}>incl. GST {inr(o.gst_amount)}</div></td>
                  <td style={td}>{o.company_name}<div style={{ fontSize: 11, color: T.text2 }}>{o.gstin || 'No GSTIN'}</div></td>
                  <td style={{ ...td, textTransform: 'capitalize', fontWeight: 600, color: o.status === 'paid' ? '#15803d' : o.status === 'pending' ? '#b45309' : T.text2 }}>{o.status}{o.paid_at && <div style={{ fontSize: 11, fontWeight: 400, color: T.text2, textTransform: 'none' }}>{fd(o.paid_at)} · {o.provider_payment_id || ''}</div>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {o.status === 'pending' && (paying === o.id ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input value={ref} onChange={e => setRef(e.target.value)} placeholder="UTR / payment ref" style={{ ...inputS(T), width: 150, padding: '5px 8px' }} autoFocus />
                        <button onClick={() => act(`/subscription-orders/${o.id}/mark-paid`, { payment_ref: ref })} style={small('#15803d', '#fff')}>Confirm</button>
                        <button onClick={() => setPaying(null)} style={small(T.bg, T.text2)}>×</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => { setPaying(o.id); setRef(''); }} style={small('#dcfce7', '#15803d')}>Mark paid</button>
                        <button onClick={() => act(`/subscription-orders/${o.id}/cancel`)} style={small('#fee2e2', '#b91c1c')}>Cancel</button>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const EMPTY_PROMO = { code: '', description: '', discount_type: 'percent', discount_value: '', max_discount: '', min_amount: '', applies_to: 'all', valid_from: '', valid_to: '', max_uses: '', per_firm_limit: 1 };

function Promos({ T, saFetch, showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(async () => { const r = await saFetch('/promo-codes'); setRows(r.ok ? await r.json() : []); }, [saFetch]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { ...form };
    ['discount_value', 'max_discount', 'min_amount', 'max_uses', 'per_firm_limit'].forEach(k => { body[k] = body[k] === '' ? null : Number(body[k]); });
    const res = await saFetch('/promo-codes', { method: 'POST', body: JSON.stringify(body) });
    const b = await res.json().catch(() => ({}));
    if (!res.ok) { showToast(b.message || 'Could not save', 'error'); return; }
    showToast(`Promo code ${b.code} created`); setForm(null); load();
  };
  const toggle = async (p) => {
    const res = await saFetch(`/promo-codes/${p.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !p.is_active }) });
    if (!res.ok) showToast('Could not update', 'error'); load();
  };
  const remove = async (p) => {
    const res = await saFetch(`/promo-codes/${p.id}`, { method: 'DELETE' });
    const b = await res.json().catch(() => ({}));
    showToast(b.message || 'Done', res.ok ? 'success' : 'error'); load();
  };
  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(x => ({ ...x, [k]: e.target.value })) });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {!form && <button onClick={() => setForm({ ...EMPTY_PROMO })} style={small(T.navy, '#fff')}>+ New promo code</button>}
      </div>
      {form && (
        <div style={{ ...card(T), padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 14 }}>
            <div><label style={labelS(T)}>Code *</label><input {...f('code')} onChange={e => setForm(x => ({ ...x, code: e.target.value.toUpperCase() }))} style={inputS(T)} placeholder="DIWALI10" /></div>
            <div><label style={labelS(T)}>Type</label><select {...f('discount_type')} style={inputS(T)}><option value="percent">Percent %</option><option value="flat">Flat ₹</option></select></div>
            <div><label style={labelS(T)}>Discount *</label><input type="number" min={0} {...f('discount_value')} style={inputS(T)} /></div>
            <div><label style={labelS(T)}>Max discount ₹</label><input type="number" min={0} {...f('max_discount')} style={inputS(T)} placeholder="No cap" /></div>
            <div><label style={labelS(T)}>Min order ₹</label><input type="number" min={0} {...f('min_amount')} style={inputS(T)} placeholder="None" /></div>
            <div><label style={labelS(T)}>Applies to</label><select {...f('applies_to')} style={inputS(T)}><option value="all">Renew & upgrade</option><option value="renew">Renew only</option><option value="upgrade">Upgrade only</option></select></div>
            <div><label style={labelS(T)}>Valid from</label><input type="date" {...f('valid_from')} style={inputS(T)} /></div>
            <div><label style={labelS(T)}>Valid to</label><input type="date" {...f('valid_to')} style={inputS(T)} /></div>
            <div><label style={labelS(T)}>Total uses</label><input type="number" min={1} {...f('max_uses')} style={inputS(T)} placeholder="Unlimited" /></div>
            <div><label style={labelS(T)}>Uses per firm</label><input type="number" min={1} {...f('per_firm_limit')} style={inputS(T)} placeholder="Unlimited" /></div>
          </div>
          <div style={{ marginTop: 14 }}><label style={labelS(T)}>Description (shown to the firm)</label><input {...f('description')} style={inputS(T)} placeholder="Festive offer" /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={() => setForm(null)} style={small(T.bg, T.text2)}>Cancel</button>
            <button onClick={save} style={small(T.orange, '#fff')}>Create code</button>
          </div>
        </div>
      )}
      <div style={{ ...card(T), overflowX: 'auto' }}>
        {rows === null ? <p style={{ padding: 24, color: T.text2, fontSize: 13 }}>Loading…</p> : rows.length === 0 ? <p style={{ padding: 24, color: T.text2, fontSize: 13 }}>No promo codes yet.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>{['Code', 'Discount', 'Applies to', 'Valid', 'Used', 'Status', ''].map(h => <th key={h} style={th(T)}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={td}><b>{p.code}</b><div style={{ fontSize: 11, color: T.text2 }}>{p.description || ''}{p.firm_name ? ` · only ${p.firm_name}` : ''}</div></td>
                  <td style={td}>{p.discount_type === 'percent' ? `${Number(p.discount_value)}%` : inr(p.discount_value)}{p.max_discount ? <div style={{ fontSize: 11, color: T.text2 }}>max {inr(p.max_discount)}</div> : null}{p.min_amount ? <div style={{ fontSize: 11, color: T.text2 }}>min order {inr(p.min_amount)}</div> : null}</td>
                  <td style={{ ...td, textTransform: 'capitalize' }}>{p.applies_to === 'all' ? 'Renew & upgrade' : p.applies_to}</td>
                  <td style={td}>{p.valid_from || p.valid_to ? `${fd(p.valid_from)} – ${fd(p.valid_to)}` : 'Always'}</td>
                  <td style={td}>{p.used_count}{p.max_uses ? ` / ${p.max_uses}` : ''}<div style={{ fontSize: 11, color: T.text2 }}>{p.per_firm_limit ? `${p.per_firm_limit} per firm` : 'no per-firm limit'}</div></td>
                  <td style={{ ...td, fontWeight: 600, color: p.is_active ? '#15803d' : T.text2 }}>{p.is_active ? 'Active' : 'Off'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => toggle(p)} style={{ ...small(T.bg, T.text), marginRight: 6 }}>{p.is_active ? 'Switch off' : 'Switch on'}</button>
                    <button onClick={() => remove(p)} style={small('#fee2e2', '#b91c1c')}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Pricing({ T, saFetch, showToast }) {
  const [p, setP] = useState(null);
  const [durations, setDurations] = useState('');
  const [discounts, setDiscounts] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await saFetch('/pricing');
      if (!r.ok) return;
      const b = await r.json();
      setP(b); setDurations(b.durations.join(', ')); setDiscounts(b.duration_discounts || {});
    })();
  }, [saFetch]);

  const save = async () => {
    setBusy(true);
    const list = durations.split(',').map(x => parseInt(x.trim(), 10)).filter(Boolean);
    const res = await saFetch('/pricing', { method: 'PUT', body: JSON.stringify({ ...p, durations: list, duration_discounts: discounts }) });
    const b = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { showToast(b.message || 'Could not save', 'error'); return; }
    setP(b); setDurations(b.durations.join(', ')); setDiscounts(b.duration_discounts || {});
    showToast('Price list saved');
  };

  if (!p) return <p style={{ color: T.text2, fontSize: 13 }}>Loading…</p>;
  const list = durations.split(',').map(x => parseInt(x.trim(), 10)).filter(Boolean);
  const num = (k) => ({ type: 'number', value: p[k], onChange: e => setP(x => ({ ...x, [k]: e.target.value })), style: inputS(T) });

  return (
    <div style={{ ...card(T), padding: 24, maxWidth: 640 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div><label style={labelS(T)}>Price per user / month (₹)</label><input {...num('price_per_user_month')} min={1} /></div>
        <div><label style={labelS(T)}>GST %</label><input {...num('gst_percent')} min={0} max={50} /></div>
        <div><label style={labelS(T)}>Minimum users</label><input {...num('min_users')} min={1} /></div>
        <div><label style={labelS(T)}>Maximum users</label><input {...num('max_users')} min={1} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelS(T)}>Durations offered (months, comma separated)</label><input value={durations} onChange={e => setDurations(e.target.value)} style={inputS(T)} /></div>
      </div>
      {list.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <label style={labelS(T)}>Discount for longer terms (%)</label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {list.map(m => (
              <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span style={{ color: T.text2 }}>{m} mo</span>
                <input type="number" min={0} max={89} value={discounts[m] ?? ''} placeholder="0"
                  onChange={e => setDiscounts(d => ({ ...d, [m]: e.target.value === '' ? undefined : Number(e.target.value) }))} style={{ ...inputS(T), width: 70 }} />
              </div>
            ))}
          </div>
        </div>
      )}
      <p style={{ fontSize: 12, color: T.text2, margin: '16px 0 0' }}>
        Example: 5 users × 12 months = {inr(5 * Number(p.price_per_user_month || 0) * 12)} + GST {Number(p.gst_percent || 0)}%.
        Upgrades charge the extra users only for the days left in the current term.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={save} disabled={busy} style={{ ...small(T.orange, '#fff'), padding: '9px 20px', opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Save price list'}</button>
      </div>
    </div>
  );
}
